import { useState } from "react";

import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";

import type { Account } from "~/types/contabilidad";

interface AccountCodeComboboxProps {
  accounts: Account[];
  /** Selected account code, or "" for "todas". */
  value: string;
  onChange: (code: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Account picker keyed by account **code** (not id), used to define the
 * endpoints of an account-code range in the Libro Diario report. Codes are
 * fixed-width 7-digit numeric strings, so a lexicographic range is numeric.
 */
export function AccountCodeCombobox({
  accounts,
  value,
  onChange,
  placeholder = "Todas las cuentas",
  disabled,
}: AccountCodeComboboxProps) {
  const [open, setOpen] = useState(false);

  const selected = accounts.find((a) => a.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-9 w-[240px] justify-between rounded-xl font-normal"
        >
          <span className="truncate">
            {selected ? `${selected.code} - ${selected.name}` : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar cuenta..." />
          <CommandList>
            <CommandEmpty>No se encontró cuenta.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="todas"
                onSelect={() => {
                  onChange("");
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === "" ? "opacity-100" : "opacity-0"
                  )}
                />
                {placeholder}
              </CommandItem>
              {accounts.map((account) => (
                <CommandItem
                  key={account.id}
                  value={`${account.code} ${account.name}`}
                  onSelect={() => {
                    onChange(account.code);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === account.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {account.code} - {account.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
